import { getSelectionContext } from "./source";
import {
    createTextRange,
    getRenderedTextContent,
    trimRenderedTextVisualRange,
} from "./rendered-text";
import type {
    AnnotationStatus,
    PreviewAnnotation,
    RenderedAreaAnnotationTarget,
    RenderedBlockAnnotationTarget,
    RenderedElementAnnotationTarget,
    RenderedTextAnnotationTarget,
} from "./types";

interface RelocateRenderedAnnotationsOptions {
    documentPath: string;
    root: HTMLElement;
    annotations: PreviewAnnotation[];
    now?: string;
}

interface RelocateRenderedAnnotationsResult {
    annotations: PreviewAnnotation[];
    changed: boolean;
}

interface MatchCandidate {
    startOffset: number;
    score: number;
}

export function relocateRenderedAnnotations({
    documentPath,
    root,
    annotations,
    now = new Date().toISOString(),
}: RelocateRenderedAnnotationsOptions): RelocateRenderedAnnotationsResult {
    let changed = false;
    const next = annotations.map((annotation) => {
        const target = annotation.target;
        if (target.documentPath !== documentPath) return annotation;
        if (target.kind === "rendered-text") {
            const relocated = relocateRenderedTextAnnotation(root, annotation, target, now);
            if (relocated !== annotation) changed = true;
            return relocated;
        }
        if (
            target.kind === "rendered-block" ||
            target.kind === "rendered-area" ||
            target.kind === "rendered-element"
        ) {
            const relocated = relocateRenderedBlockBackedAnnotation(root, annotation, target, now);
            if (relocated !== annotation) changed = true;
            return relocated;
        }
        return annotation;
    });
    return { annotations: next, changed };
}

function relocateRenderedTextAnnotation(
    rootBoundary: HTMLElement,
    annotation: PreviewAnnotation,
    target: RenderedTextAnnotationTarget,
    now: string,
): PreviewAnnotation {
    const root = findRenderedTextRootByBlockId(rootBoundary, target.blockId);
    if (!root) {
        return findRenderedBlockRootByBlockId(rootBoundary, target.blockId)
            ? annotation
            : markOrphaned(annotation, now);
    }
    const currentHash = root.dataset.mdRenderedOutputHash;
    const content = getRenderedTextContent(root);
    const quote = target.quote;
    if (!quote) return annotation;

    const currentSlice = content.slice(target.startOffset, target.endOffset);
    if (currentSlice === quote) {
        return updateRenderedTextTarget(annotation, target, {
            startOffset: target.startOffset,
            endOffset: target.endOffset,
            content,
            outputHash: currentHash,
            now,
        });
    }

    const relocatedStart = findRelocatedStart(content, target);
    if (relocatedStart == null) {
        return markOrphaned(annotation, now);
    }
    return updateRenderedTextTarget(annotation, target, {
        startOffset: relocatedStart,
        endOffset: relocatedStart + quote.length,
        content,
        outputHash: currentHash,
        now,
    });
}

function updateRenderedTextTarget(
    annotation: PreviewAnnotation,
    target: RenderedTextAnnotationTarget,
    options: {
        startOffset: number;
        endOffset: number;
        content: string;
        outputHash?: string;
        now: string;
    },
): PreviewAnnotation {
    const context = getSelectionContext(options.content, options.startOffset, options.endOffset);
    const nextTarget: RenderedTextAnnotationTarget = {
        ...target,
        outputHash: options.outputHash ?? target.outputHash,
        startOffset: options.startOffset,
        endOffset: options.endOffset,
        quote: context.quote || target.quote,
        prefix: context.prefix,
        suffix: context.suffix,
    };
    const status = restoreStatus(annotation);
    if (
        status === annotation.status &&
        !annotation.statusBeforeOrphaned &&
        renderedTextTargetEqual(target, nextTarget)
    ) {
        return annotation;
    }
    return {
        ...annotation,
        status,
        statusBeforeOrphaned: undefined,
        target: nextTarget,
        updatedAt: options.now,
    };
}

function relocateRenderedBlockBackedAnnotation(
    rootBoundary: HTMLElement,
    annotation: PreviewAnnotation,
    target: RenderedBlockAnnotationTarget | RenderedAreaAnnotationTarget | RenderedElementAnnotationTarget,
    now: string,
): PreviewAnnotation {
    const block = findRenderedBlockRootByBlockId(rootBoundary, target.blockId);
    if (!block) return markOrphaned(annotation, now);
    const currentHash = readRenderedBlockOutputHash(block);
    if (target.outputHash && currentHash && target.outputHash !== currentHash) {
        return markOrphaned(annotation, now);
    }
    if (target.kind === "rendered-element" && !findRenderedElementTarget(block, target.selector)) {
        return markOrphaned(annotation, now);
    }
    const status = restoreStatus(annotation);
    const nextTarget = currentHash && target.outputHash !== currentHash
        ? { ...target, outputHash: currentHash }
        : target;
    if (
        status === annotation.status &&
        !annotation.statusBeforeOrphaned &&
        nextTarget === target
    ) {
        return annotation;
    }
    return {
        ...annotation,
        status,
        statusBeforeOrphaned: undefined,
        target: nextTarget,
        updatedAt: now,
    };
}

function findRenderedElementTarget(block: HTMLElement, selector: string): HTMLElement | null {
    if (selector.startsWith("key:")) {
        return block.querySelector<HTMLElement>(
            `[data-md-annotation-key="${cssAttributeValue(selector.slice(4))}"]`,
        );
    }
    if (!selector.startsWith("path:")) return null;
    try {
        return block.querySelector<HTMLElement>(selector.slice(5));
    } catch {
        return null;
    }
}

function findRenderedTextRootByBlockId(root: HTMLElement, blockId: string): HTMLElement | null {
    return root.querySelector(
        `[data-md-rendered-text-root][data-md-rendered-text-block-id="${cssAttributeValue(blockId)}"]`,
    );
}

export function findRenderedBlockRootByBlockId(root: HTMLElement, blockId: string): HTMLElement | null {
    return root.querySelector(
        `[data-md-annotation-block-id="${cssAttributeValue(blockId)}"]`,
    );
}

export function readRenderedBlockOutputHash(block: HTMLElement): string | undefined {
    const direct = block.dataset.mdRenderedOutputHash;
    if (direct) return direct;
    const child = block.querySelector("[data-md-rendered-output-hash]") as HTMLElement | null;
    return child?.dataset.mdRenderedOutputHash;
}

export function getRenderedTextAnnotationRect(
    rootBoundary: HTMLElement,
    target: RenderedTextAnnotationTarget,
): DOMRect | null {
    const root = findRenderedTextRootByBlockId(rootBoundary, target.blockId);
    if (!root) return null;
    const visualRange = trimRenderedTextVisualRange(target);
    const range = createTextRange(root, visualRange.startOffset, visualRange.endOffset);
    return range?.getBoundingClientRect?.() ?? root.getBoundingClientRect();
}

function markOrphaned(annotation: PreviewAnnotation, now: string): PreviewAnnotation {
    if (annotation.status === "orphaned") return annotation;
    return {
        ...annotation,
        status: "orphaned",
        statusBeforeOrphaned: annotation.status,
        updatedAt: now,
    };
}

function restoreStatus(annotation: PreviewAnnotation): AnnotationStatus {
    if (annotation.status !== "orphaned") return annotation.status;
    return annotation.statusBeforeOrphaned ?? "open";
}

function findRelocatedStart(
    content: string,
    target: RenderedTextAnnotationTarget,
): number | null {
    const quote = target.quote;
    const candidates: MatchCandidate[] = [];
    let cursor = 0;
    while (cursor <= content.length) {
        const found = content.indexOf(quote, cursor);
        if (found < 0) break;
        candidates.push({
            startOffset: found,
            score: contextScore(content, found, found + quote.length, target),
        });
        cursor = found + Math.max(1, quote.length);
    }
    if (candidates.length === 0) return null;
    if (candidates.length === 1) return candidates[0]?.startOffset ?? null;
    candidates.sort((a, b) => b.score - a.score);
    const best = candidates[0];
    const second = candidates[1];
    if (!best || !second || best.score === 0 || best.score === second.score) return null;
    return best.startOffset;
}

function contextScore(
    content: string,
    startOffset: number,
    endOffset: number,
    target: RenderedTextAnnotationTarget,
): number {
    const prefix = target.prefix ?? "";
    const suffix = target.suffix ?? "";
    const before = content.slice(Math.max(0, startOffset - prefix.length), startOffset);
    const after = content.slice(endOffset, Math.min(content.length, endOffset + suffix.length));
    return commonSuffixLength(prefix, before) + commonPrefixLength(suffix, after);
}

function commonPrefixLength(a: string, b: string): number {
    const length = Math.min(a.length, b.length);
    let count = 0;
    while (count < length && a[count] === b[count]) count += 1;
    return count;
}

function commonSuffixLength(a: string, b: string): number {
    const length = Math.min(a.length, b.length);
    let count = 0;
    while (
        count < length &&
        a[a.length - count - 1] === b[b.length - count - 1]
    ) {
        count += 1;
    }
    return count;
}

function renderedTextTargetEqual(
    left: RenderedTextAnnotationTarget,
    right: RenderedTextAnnotationTarget,
): boolean {
    return (
        left.outputHash === right.outputHash &&
        left.startOffset === right.startOffset &&
        left.endOffset === right.endOffset &&
        left.quote === right.quote &&
        left.prefix === right.prefix &&
        left.suffix === right.suffix
    );
}

function cssAttributeValue(value: string): string {
    return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}
